import React, {useEffect, useRef, useState} from 'react';
import { Typography, Stepper, Step, StepLabel, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import BaseModal from '../../../components/utils/BaseModal';

type Props = {
  open: boolean;
  mode: 'add-mfa' | 'remove-mfa' | null;
  onClose: () => void;
  activeStep: number;
  setActiveStep: (step: number) => void;
  mfaSetupData: any;
  mfaCode: string;
  setMfaCode: (val: string) => void;
  mfaCodeValid: boolean;
  verifyingMfa: boolean;
  onSubmit: () => void;
  onBack?: () => void;
};

const CODE_LENGTH = 6;

const SecurityActionModal: React.FC<Props> = ({
                                                open, mode, onClose, activeStep, setActiveStep, mfaSetupData,
                                                mfaCode, setMfaCode, mfaCodeValid, verifyingMfa, onSubmit, onBack
                                              }) => {
  const { t } = useTranslation();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Persist mode during close animation to prevent UI flicker
  const [displayMode, setDisplayMode] = useState(mode);

  useEffect(() => {
    if (mode !== null) {
      setDisplayMode(mode);
    }
  }, [mode]);

  const isAdd = displayMode === 'add-mfa';
  const title = isAdd ? t('profile.security.mfa.setupButton') : t('profile.security.mfa.removeTitle');

  const codeArray = mfaCode.split('').concat(Array(CODE_LENGTH).fill('')).slice(0, CODE_LENGTH);

  useEffect(() => {
    if (open && (activeStep === 1 || !isAdd)) {
      setTimeout(() => inputRefs.current[0]?.focus(), 150);
    }
  }, [activeStep, isAdd, open]);

  const handleCodeChange = (index: number, value: string) => {
    const lastChar = value.slice(-1);
    if (!/^\d?$/.test(lastChar)) return;

    const newCodeArray = [...codeArray];
    newCodeArray[index] = lastChar;
    setMfaCode(newCodeArray.join(''));

    if (lastChar && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace for navigation
    if (e.key === 'Backspace') {
      if (!codeArray[index] && index > 0) {
        const newCodeArray = [...codeArray];
        newCodeArray[index - 1] = '';
        setMfaCode(newCodeArray.join(''));
        inputRefs.current[index - 1]?.focus();
      } else {
        const newCodeArray = [...codeArray];
        newCodeArray[index] = '';
        setMfaCode(newCodeArray.join(''));
      }
    }

    // Handle enter to submit if valid and not already verifying
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent potential form issues
      if (mfaCodeValid && !verifyingMfa) {
        onSubmit();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    setMfaCode(pasted);
    const nextIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
      <BaseModal
          open={open}
          onAbort={onClose}
          title={title}
          maxWidth="xs"
          minHeight="570px"
      >
        <div className="flex flex-col h-full min-h-[480px]">
          {isAdd && (
              <Stepper activeStep={activeStep} alternativeLabel className="mb-8 pt-4">
                <Step><StepLabel><span className="dark:text-neutral-400 font-medium">{t('profile.security.mfa.step.scan')}</span></StepLabel></Step>
                <Step><StepLabel><span className="dark:text-neutral-400 font-medium">{t('profile.security.mfa.step.verify')}</span></StepLabel></Step>
              </Stepper>
          )}

          {displayMode === 'remove-mfa' && (
              <div className="p-4 mb-6 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
                <Typography className="text-sm text-red-700 dark:text-red-400 font-medium">
                  {t('profile.security.mfa.removeWarning')}
                </Typography>
              </div>
          )}

          <div className="flex-grow flex flex-col items-center justify-center">
            {isAdd && activeStep === 0 ? (
                <div className="flex flex-col items-center space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-400 w-full">
                  {mfaSetupData ? (
                      <div className="p-2 bg-white border-4 border-blue-600 rounded-xl shadow-2xl">
                        <QRCode value={mfaSetupData.otpAuthUrl} size={180} />
                      </div>
                  ) : (
                      <div className="h-[180px] flex items-center justify-center italic text-gray-400">{t('profile.security.common.loading')}</div>
                  )}
                  <Typography className="text-center text-gray-500 dark:text-neutral-400 px-4 text-sm italic">
                    {t('profile.security.mfa.scanQRCode')}
                  </Typography>
                </div>
            ) : (
                <div className="flex justify-center items-center gap-2 animate-in fade-in duration-400 w-full">
                  {codeArray.map((digit, index) => (
                      <div key={index} className="relative">
                        <input
                            ref={el => (inputRefs.current[index] = el)}
                            value={digit}
                            onChange={e => handleCodeChange(index, e.target.value)}
                            onKeyDown={e => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className={`
                              w-10 h-14 text-center text-2xl font-bold rounded-lg border-2 
                              bg-transparent transition-all outline-none
                              ${digit ? 'border-dark-primary' : 'border-gray-600'}
                              focus:border-dark-primary focus:ring-2 focus:ring-dark-primary/20
                              dark:text-white
                            `}
                        />
                        {!digit && (
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-gray-600 rounded-full" />
                        )}
                      </div>
                  ))}
                </div>
            )}
          </div>

          <div className="mt-auto pt-8">
            <div className="flex space-x-3">
              {isAdd && activeStep === 1 && (
                  <Button variant="text" className="flex-1 font-bold" onClick={onBack} disabled={verifyingMfa}>
                    <div className="dark:text-gray-200 text-gray-700">{t('profile.security.common.back')}</div>
                  </Button>
              )}
              {isAdd && activeStep === 0 ? (
                  <Button
                      variant="contained"
                      fullWidth
                      onClick={() => setActiveStep(1)}
                      className="h-12 rounded-2xl font-black bg-dark-primary"
                      disableElevation
                  >
                    {t('profile.security.common.next')}
                  </Button>
              ) : (
                  <Button
                      onClick={onSubmit}
                      disabled={!mfaCodeValid || verifyingMfa}
                      variant="contained"
                      className={`flex-[2] h-12 rounded-2xl font-black shadow-lg relative ${displayMode === 'remove-mfa' ? 'bg-red-600' : 'bg-emerald-600'}`}
                      disableElevation
                  >
                    <span className={verifyingMfa ? 'invisible' : ''}>
                      {isAdd ? t('profile.security.mfa.verifyButton') : t('profile.security.mfa.removeButton')}
                    </span>
                    {verifyingMfa && (
                        <CircularProgress size={24} color="inherit" className="absolute left-1/2 top-1/2 -ml-[12px] -mt-[12px]" />
                    )}
                  </Button>
              )}
            </div>
          </div>
        </div>
      </BaseModal>
  );
};

export default SecurityActionModal;